import { cache } from "react"
import { i18nConfig } from "@/i18Config"
import type { IContentItem } from "@kontent-ai/delivery-sdk"

import type { Button as SDKButton } from "@/types/kontent-generated/types/button.generated"
import { getDeliveryClient } from "@/lib/kontent/client"
import { getWebSpotlightApplicationTree } from "@/lib/kontent/config/getApplicationTree"
import { TButton } from "@/components/ui/atoms"

import { assertItemResponse, fetchKontent } from "../helpers"
import { mapButtonToTButton } from "../mappers"

/**
 * Maps an already-fetched SDK item, avoiding a redundant Delivery API call
 * when the item was already resolved by a parent query's depth parameter.
 */
export async function GetButtonFromItem(
  item: IContentItem,
  previewMode = false
): Promise<TButton> {
  const button = item as SDKButton
  const linkedPage = button.elements.linked_page

  // When a Button is nested several levels deep (e.g. inside a rich text
  // element of a TextMedia item within a TextMediaGroup), the parent
  // query's depth budget may resolve the Button item itself but not its
  // own `linked_page` reference. Detect that case (a referenced codename
  // with no resolved item) and re-fetch the button directly so its own
  // depth parameter covers `linked_page`, instead of silently dropping it.
  if (linkedPage.value.length > 0 && linkedPage.linkedItems.length === 0) {
    return GetButton(button.system.codename, previewMode)
  }

  const { byCodename } = await getWebSpotlightApplicationTree(previewMode)
  return mapButtonToTButton(button, byCodename)
}

async function _fetchButton(
  codename: string,
  previewMode = false
): Promise<TButton> {
  return fetchKontent("Button", async () => {
    const client = getDeliveryClient(previewMode)
    const response = await client
      .item<SDKButton>(codename)
      .languageParameter(i18nConfig.defaultLocale)
      .depthParameter(1)
      .toPromise()

    const button = assertItemResponse(response, "Button")
    return GetButtonFromItem(button, previewMode)
  })
}

export const GetButton = cache(_fetchButton)
